import { msg } from "@lingui/core/macro";
import { createSignal, Show } from "solid-js";
import { Ban, Copy, Eye, KeyRound, RefreshCw, ShieldCheck } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { showToast } from "@/components/ui/toast";
import { useNavigate } from "@/lib/u-router";
import { api, createApiMutation } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../../use-admin-capabilities";
import { startImpersonation } from "../../impersonation";
import type { DirectoryUser } from "./user-detail-view";

/**
 * Generador client-side de contraseñas legibles (3 palabras + 2 dígitos, p.ej.
 * `cometa-delfin-nube-47`): fáciles de dictar/copiar al entregar la credencial
 * en persona. La contraseña se muestra UNA sola vez; el servidor solo guarda el
 * hash argon2 (mismo camino que el registro).
 */
const PASSWORD_WORDS = [
  "aurora",
  "bosque",
  "brisa",
  "cielo",
  "cometa",
  "coral",
  "cumbre",
  "delfin",
  "duna",
  "estela",
  "faro",
  "fuego",
  "islote",
  "jaguar",
  "laguna",
  "lirio",
  "luna",
  "marea",
  "monte",
  "nube",
  "oasis",
  "orbita",
  "pampa",
  "perla",
  "prisma",
  "rio",
  "roble",
  "sabana",
  "selva",
  "sol",
  "trueno",
  "valle",
  "viento",
  "volcan",
  "zafiro",
] as const;

function randomInt(maxExclusive: number): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return (buf[0] ?? 0) % maxExclusive;
}

function generateReadablePassword(): string {
  const words = Array.from(
    { length: 3 },
    () => PASSWORD_WORDS[randomInt(PASSWORD_WORDS.length)] ?? "orbita",
  );
  const digits = String(randomInt(100)).padStart(2, "0");
  return `${words.join("-")}-${digits}`;
}

/**
 * F2-C — danger zone: suspend/unsuspend and "Ver como" (impersonate). Every
 * action is capability-gated in the UI and re-checked server-side.
 */
export function DangerZoneTab(props: { user: DirectoryUser; onChanged: () => void }) {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const navigate = useNavigate();

  const banMutation = createApiMutation(async (vars: { user_id: string; reason?: string }) =>
    api.POST("/api/admin/ban-user", { body: vars }),
  );
  const setPasswordMutation = createApiMutation(async (vars: { id: string; newPassword: string }) =>
    api.POST("/api/admin/users/{id}/set-password", {
      params: { path: { id: vars.id } },
      body: { newPassword: vars.newPassword },
    }),
  );

  // Reset de contraseña: la generada vive solo en este estado local y se
  // descarta al cerrar el diálogo (se muestra UNA vez).
  const [resetOpen, setResetOpen] = createSignal(false);
  const [generated, setGenerated] = createSignal("");
  const [resetDone, setResetDone] = createSignal(false);

  const openReset = () => {
    setGenerated(generateReadablePassword());
    setResetDone(false);
    setResetOpen(true);
  };
  const closeReset = (open: boolean) => {
    if (!open) {
      setResetOpen(false);
      setGenerated("");
      setResetDone(false);
    }
  };
  const copyPassword = () => {
    void (async () => {
      try {
        await navigator.clipboard?.writeText(generated());
        showToast(t(msg`Password copied`), "success");
      } catch {
        showToast(t(msg`Could not copy`), "error");
      }
    })();
  };
  const confirmReset = () => {
    setPasswordMutation.mutate(
      { id: props.user.id, newPassword: generated() },
      {
        onSuccess: () => {
          setResetDone(true);
          showToast(t(msg`Password reset — all sessions were closed`), "success");
        },
        onError: () => {
          showToast(t(msg`Could not reset the password`), "error");
        },
      },
    );
  };
  const unbanMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/unban-user", { body: vars }),
  );

  const impersonate = () => {
    void (async () => {
      try {
        const name = await startImpersonation(props.user.id);
        showToast(t(msg`Now viewing as ${name}`), "success");
        // Land on the learner home to see the app AS the student.
        await navigate({ to: "/learn" });
      } catch {
        showToast(t(msg`Could not start impersonation`), "error");
      }
    })();
  };

  return (
    <Card class="mt-4 border-destructive/30">
      <CardContent class="p-5 space-y-4">
        {/* ── Impersonation ── */}
        <Show when={can("users.impersonate")}>
          <div class="flex items-center justify-between gap-3">
            <div>
              <div class="text-sm font-semibold">{t(msg`View as this user`)}</div>
              <div class="text-xs text-muted-foreground">
                {t(msg`Opens a read-only impersonated session with an audit trail.`)}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="impersonate-button"
              onClick={impersonate}
            >
              <Eye class="mr-1.5 h-4 w-4" />
              {t(msg`View as`)}
            </Button>
          </div>
        </Show>

        {/* ── Reset password ── */}
        <Show when={can("users.setPassword")}>
          <div class="flex items-center justify-between gap-3 border-t border-border pt-4">
            <div>
              <div class="text-sm font-semibold">{t(msg`Reset password`)}</div>
              <div class="text-xs text-muted-foreground">
                {t(msg`Set a new password and close every session of this user.`)}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="reset-password-button"
              onClick={openReset}
            >
              <KeyRound class="mr-1.5 h-4 w-4" />
              {t(msg`Reset password`)}
            </Button>
          </div>
        </Show>

        {/* ── Suspend / reactivate ── */}
        <Show when={can("users.ban")}>
          <div class="flex items-center justify-between gap-3 border-t border-border pt-4">
            <div>
              <div class="text-sm font-semibold">
                {props.user.banned ? t(msg`Reactivate account`) : t(msg`Suspend account`)}
              </div>
              <div class="text-xs text-muted-foreground">
                {props.user.banned
                  ? t(msg`Restore the user's ability to sign in.`)
                  : t(msg`Prevent the user from signing in.`)}
              </div>
            </div>
            <Show
              when={props.user.banned}
              fallback={
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={banMutation.isPending}
                  onClick={() =>
                    banMutation.mutate(
                      { user_id: props.user.id },
                      {
                        onSuccess: () => {
                          props.onChanged();
                          showToast(t(msg`User suspended`), "success");
                        },
                      },
                    )
                  }
                >
                  <Ban class="mr-1.5 h-4 w-4" />
                  {t(msg`Suspend`)}
                </Button>
              }
            >
              <Button
                variant="outline"
                size="sm"
                disabled={unbanMutation.isPending}
                onClick={() =>
                  unbanMutation.mutate(
                    { userId: props.user.id },
                    {
                      onSuccess: () => {
                        props.onChanged();
                        showToast(t(msg`User reactivated`), "success");
                      },
                    },
                  )
                }
              >
                <ShieldCheck class="mr-1.5 h-4 w-4" />
                {t(msg`Reactivate`)}
              </Button>
            </Show>
          </div>
        </Show>
      </CardContent>

      {/* ── Reset password dialog: la contraseña se muestra UNA sola vez ── */}
      <Dialog open={resetOpen()} onOpenChange={closeReset} contentClass="max-w-[460px] p-6">
        <DialogTitle class="text-xl font-bold mb-2">{t(msg`Reset password`)}</DialogTitle>
        <DialogDescription class="text-sm text-muted-foreground mb-4">
          {resetDone()
            ? t(
                msg`Done. Hand this password to the user now — it will not be shown again and all their sessions were closed.`,
              )
            : (() => {
                const { email } = props.user;
                return t(
                  msg`A new password will be set for ${email} and all their sessions will be closed. It is shown only once.`,
                );
              })()}
        </DialogDescription>
        <div class="flex items-center gap-2">
          <code
            class="flex-1 rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm select-all"
            data-testid="generated-password"
          >
            {generated()}
          </code>
          <Show when={!resetDone()}>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t(msg`Generate another password`)}
              data-testid="regenerate-password-button"
              onClick={() => setGenerated(generateReadablePassword())}
            >
              <RefreshCw class="h-4 w-4" />
            </Button>
          </Show>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t(msg`Copy password`)}
            data-testid="copy-password-button"
            onClick={copyPassword}
          >
            <Copy class="h-4 w-4" />
          </Button>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <Show
            when={resetDone()}
            fallback={
              <>
                <Button variant="ghost" onClick={() => closeReset(false)}>
                  {t(msg`Cancel`)}
                </Button>
                <Button
                  variant="destructive"
                  disabled={setPasswordMutation.isPending}
                  data-testid="confirm-reset-password-button"
                  onClick={confirmReset}
                >
                  <KeyRound class="mr-1.5 h-4 w-4" />
                  {t(msg`Reset password`)}
                </Button>
              </>
            }
          >
            <Button data-testid="close-reset-password-button" onClick={() => closeReset(false)}>
              {t(msg`Done`)}
            </Button>
          </Show>
        </div>
      </Dialog>
    </Card>
  );
}
